import {useStylesECDS} from '../ecds/ecdsStyle';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import React, {useEffect, useRef, useState} from 'react';
import Formatter from '../../../../util/formatter';
import _ from 'lodash';
import moment from 'moment/moment';
import uuidv4 from 'uuid/v4';
import Notes from '../../../../util/notes';
import {Checkbox, FormControlLabel, Grid, Typography} from '@mui/material';
import {ClinicalFrailtyScale} from '../../../../controls/agyle/dictionaries';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {clinicalFrailtyOutcomeDict} from '../../../../store/reducers/dicts';

export const RockwoodForm = (props) => {
    const classes = useStylesECDS();
    const {t} = useTranslation();

    const outcome = useSelector(clinicalFrailtyOutcomeDict);
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    const [dataForm, setDataForm] = useState({
        scale: null,
        action: '',
        patientConsentToReferral: false,
        referralCompleted: false,
        details: '',
    });
    const firstUpdate = useRef(true);

    useEffect(() => {
        if (!_.isNil(props.selectedForm)) {
            setDataForm({
                ...props.selectedForm,
            });
        }
    }, []);

    useEffect(() => {
        if (!dataForm.scale) {
            return;
        }
        let data;

        const outcomeId = dataForm?.scale?.outcome;
        const score = Number(dataForm?.scale?.__code);

        if (score < 4) {
            data = {
                patientConsentToReferral: false,
                referralCompleted: false,
            };
            data = {...data, 'action': getOutcome(outcomeId || '1')};
        } else if (score >= 4 && score <= 5) {
            data = {...data, 'action': getOutcome(outcomeId || '2')};
        } else {
            data = {
                patientConsentToReferral: false,
                referralCompleted: false,
            };
            data = {...data, 'action': getOutcome(outcomeId ||'3')};
        }
        setDataForm(prevState => ({...prevState, ...data}));
    }, [dataForm.scale]);

    useEffect(() => {
        if (!firstUpdate.current) {
            const dateOfForm = moment().toISOString();

            const id = uuidv4() + '';
            const data = [{
                name: 'studies.rockwood',
                value: {
                    ...dataForm,
                    id: id,
                    lastScore: checkLastScore(),
                    date: dateOfForm,
                    by: userFullName,
                    login: userObject?.userName,
                    parentForm: props.parentForm,
                },
                noteToAddWithRefId: prepareNote(),
                referenceId: id,
            }];
            props.save(data);
        }
    }, [dataForm]);

    const getOutcome = (score) => {
        let result = _.find(outcome, {__code: score});
        if (result) {
            return result?.__description;
        }
        return '-';
    };

    const handleChange = (event) => {
        const {name, value} = event.target;
        firstUpdate.current = false;
        setDataForm(prevState => ({...prevState, [name]: value}));
    };

    const checkboxHandler = (event) => {
        const {name, checked} = event.target;
        firstUpdate.current = false;
        setDataForm(prevState => ({...prevState, [name]: checked}));
    };

    const checkLastScore = () => {
        if (!_.isEmpty(props.patient?.studies?.rockwood?.list)) {
            let lastScore;
            for (let i = 0; i < props.patient?.studies?.rockwood?.list.length; i++) {
                if (!props.patient?.studies?.rockwood?.list[i].canceled) {
                    lastScore = props.patient.studies.rockwood.list[i]?.scale?.__code;
                    break;
                }

            }
            return lastScore ? lastScore : null;
        }
    };

    const prepareNote = () => {
        const lastScore = checkLastScore();
        return Notes.getClinicalFrailtyScoreNote(dataForm, lastScore, props.parentForm);
    };

    const setDataDictionary = (data) => {
        handleChange({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    return (
        <div className={classes.root}>
            <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
            >
                <Grid item xs={12}>
                    <ClinicalFrailtyScale
                        agyleRefresh
                        label={t('Scale')}
                        value={dataForm.scale}
                        isOptionEqualToValue={(option, value) => option.__code === value.__code}
                        onChange={(e) => setDataDictionary(['scale', e])}
                        error={!dataForm.scale}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        name="Information"
                        label={t('Information')}
                        readOnly
                        onChange={() => null}
                        multiline
                        fullWidth
                        rows={10}
                        value={dataForm.action || ''}
                        InputProps={{readOnly: true}}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>
            </Grid>

            <Grid
                container
                spacing={2}
                direction="row-reverse"
                justifyContent="space-between"
                alignItems="center"
                sx={{py: 2}}
            >
                <Grid item xs={12} md={9}>
                    <AgyleTextField
                        fullWidth
                        name="details"
                        label={t('Additional Details')}
                        onChange={handleChange}
                        value={dataForm.details || ''}
                        size="large"
                        noBorder={false}
                        withNewBackground
                        debounce
                    />
                </Grid>

                {(dataForm?.scale?.__code == 4 || dataForm?.scale?.__code == 5) &&
                    <Grid item xs={12} md={3}>
                        <FormControlLabel
                            style={{margin: '10px'}}
                            control={
                                <Checkbox
                                    name="patientConsentToReferral"
                                    checked={dataForm.patientConsentToReferral}
                                    onChange={checkboxHandler}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" color="textSecondary">
                                    {t('Patient consent to referral')}
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            style={{marginLeft: 10}}
                            control={
                                <Checkbox
                                    name="referralCompleted"
                                    checked={dataForm.referralCompleted}
                                    onChange={checkboxHandler}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" color="textSecondary">
                                    {t('Referral completed')}
                                </Typography>
                            }
                        />
                    </Grid>
                }
            </Grid>
        </div>
    );
};